<template>
  <div class="immediate-payment-split">
    <p class="text-muted small mb-2">{{ $t("saisiePaiement.splitPaymentsHint") }}</p>

    <div v-for="(line, idx) in paymentLines" :key="line.uid" class="payment-split-card mb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <strong>{{ $t("saisiePaiement.paymentLine", { n: idx + 1 }) }}</strong>
        <a-button
          v-if="paymentLines.length > 1"
          type="link"
          size="small"
          class="text-danger p-0"
          @click="removePaymentLine(idx)"
        >
          {{ $t("saisiePaiement.removePaymentLine") }}
        </a-button>
      </div>
      <a-row :gutter="[12, 8]">
        <a-col :xs="24" :sm="12">
          <div class="mb-2">
            <label class="filter-label">{{ $t("saisiePaiement.selectMethod") }}</label>
            <a-select v-model="line.method" style="width: 100%" @change="onLineMethodChange(idx)">
              <a-select-option value="cash">{{ $t("decaissement.paymentMethod.cash") }}</a-select-option>
              <a-select-option value="cheque">{{ $t("decaissement.paymentMethod.cheque") }}</a-select-option>
              <a-select-option value="virement">{{ $t("decaissement.paymentMethod.virement") }}</a-select-option>
              <a-select-option value="traite">{{ $t("decaissement.paymentMethod.traite") }}</a-select-option>
              <a-select-option value="tpe">{{ $t("decaissement.paymentMethod.tpe") }}</a-select-option>
              <a-select-option value="prelevement">{{ $t("decaissement.paymentMethod.prelevement") }}</a-select-option>
            </a-select>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12">
          <div
            class="mb-2"
            :class="{
              'payment-field--insufficient': lineAmountExceedsAvailable(line),
            }"
          >
            <label class="filter-label">{{ $t("recette.montant") }}</label>
            <a-input-number v-model="line.amount" :min="0" :precision="3" style="width: 100%" />
          </div>
        </a-col>
      </a-row>
      <a-row :gutter="[12, 8]">
        <a-col :span="24">
          <template v-if="line.method === 'cash'">
            <div class="mb-2">
              <label class="filter-label payment-fund-label">{{ $t("decaissement.fundSource") }}</label>
              <a-select
                v-model="line.fundDestination"
                style="width: 100%"
                @change="onLineFundDestinationChange(idx)"
              >
                <a-select-option value="myCaisse" :disabled="!myCaisseId">{{
                  $t("decaissement.myCaisse")
                }}</a-select-option>
                <a-select-option value="otherCaisse" :disabled="!depenseOtherCashOptions.length">{{
                  $t("decaissement.otherCaisse")
                }}</a-select-option>
              </a-select>
            </div>
            <div
              v-if="line.fundDestination === 'myCaisse'"
              class="mb-2 payment-fund-stack"
              :class="{
                'payment-field--insufficient':
                  line.caisseId && lineCashAmountExceedsCaisse(line),
              }"
            >
              <label class="filter-label payment-fund-label">{{ $t("decaissement.cashName") }}</label>
              <!-- Enabled as soon as the user owns more than one caisse. -->
              <a-select
                v-model="line.caisseId"
                style="width: 100%"
                :disabled="myCaisses.length <= 1"
                :placeholder="$t('decaissement.validation.selectCaisse')"
              >
                <a-select-option v-for="c in myCaisses" :key="c._id" :value="c._id">
                  {{ c.name }} — {{ cashAvailabilityForCaisse(c, line.method) }}
                </a-select-option>
              </a-select>
            </div>
            <div
              v-if="line.fundDestination === 'otherCaisse'"
              class="mb-2 payment-fund-stack"
              :class="{
                'payment-field--insufficient':
                  line.caisseId && lineCashAmountExceedsCaisse(line),
              }"
            >
              <label class="filter-label payment-fund-label">{{ $t("decaissement.cashName") }}</label>
              <a-select v-model="line.caisseId" style="width: 100%" :placeholder="$t('decaissement.validation.selectCaisse')">
                <a-select-option v-for="c in depenseOtherCashOptions" :key="c._id" :value="c._id">
                  {{ c.name }} — {{ cashAvailabilityForCaisse(c, line.method) }}
                </a-select-option>
              </a-select>
            </div>
            <p v-if="line.caisseId" class="text-muted small mb-0 mt-1">
              {{ $t("decaissement.availableForThisPayment") }}:
              <strong>{{ cashAvailabilityFormatted(line) }}</strong>
            </p>
            <p v-if="line.caisseId && lineCashAmountExceedsCaisse(line)" class="small mb-0 mt-1 text-danger">
              {{ $t("decaissement.caisseBalanceExceedsAvailWarning") }}
            </p>
          </template>
          <div
            v-else
            class="mb-2 payment-fund-stack"
            :class="{
              'payment-field--insufficient':
                line.caisseBankId && lineBankAmountExceedsCaisse(line),
            }"
          >
            <label class="filter-label payment-fund-label">{{ $t("decaissement.caisseBank") }}</label>
            <a-select
              v-model="line.caisseBankId"
              allow-clear
              style="width: 100%"
              :placeholder="$t('decaissement.validation.selectCaisseBank')"
            >
              <a-select-option v-for="b in caisseBanksList" :key="b._id" :value="b._id">
                {{ b.name }} — {{ bankAvailabilityForCaisse(b, line.method) }}
              </a-select-option>
            </a-select>
            <p v-if="line.caisseBankId" class="text-muted small mb-0 mt-1">
              {{ $t("decaissement.availableForThisPayment") }}:
              <strong>{{ bankAvailabilityFormatted(line) }}</strong>
            </p>
            <p v-if="line.caisseBankId && lineBankAmountExceedsCaisse(line)" class="small mb-0 mt-1 text-danger">
              {{ $t("decaissement.caisseBalanceExceedsAvailWarning") }}
            </p>
          </div>
        </a-col>
      </a-row>

      <pay-cheque-section
        v-if="line.method === 'cheque'"
        :hide-bank-column="true"
        :rows="line.chequeRows"
        :total-required="Number(line.amount) || 0"
        @update="(i, f, v) => updateLineDetailRow(idx, 'chequeRows', i, f, v)"
        @add="addLineDetailRow(idx, 'chequeRows')"
        @remove="removeLineDetailRow(idx, 'chequeRows', $event)"
      />
      <pay-traite-section
        v-if="line.method === 'traite'"
        :hide-bank-column="true"
        :rows="line.traiteRows"
        :total-required="Number(line.amount) || 0"
        @update="(i, f, v) => updateLineDetailRow(idx, 'traiteRows', i, f, v)"
        @add="addLineDetailRow(idx, 'traiteRows')"
        @remove="removeLineDetailRow(idx, 'traiteRows', $event)"
      />
      <pay-virement-section
        v-if="line.method === 'virement'"
        :rows="line.virementRows"
        :total-required="Number(line.amount) || 0"
        @update="(i, f, v) => updateLineDetailRow(idx, 'virementRows', i, f, v)"
        @add="addLineDetailRow(idx, 'virementRows')"
        @remove="removeLineDetailRow(idx, 'virementRows', $event)"
      />
    </div>

    <a-button type="dashed" block icon="plus" class="mb-3" @click="addPaymentLine">
      {{ $t("saisiePaiement.addPaymentLine") }}
    </a-button>

    <div class="sum-bar mb-3" :class="{ match: splitsSumOk, mismatch: !splitsSumOk }">
      <span
        >{{ $t("saisiePaiement.splitsTotal") }}: {{ splitsSum.toFixed(3) }} /
        {{ resolvedTotal().toFixed(3) }}</span
      >
      <a-tag :color="splitsSumOk ? 'green' : 'red'">{{
        splitsSumOk ? "OK" : $t("saisiePaiement.splitsSumMismatch")
      }}</a-tag>
    </div>

    <div class="row">
      <div class="col-md-6">
        <a-form-item :label="$t('decaissement.receiptNumber')">
          <a-input v-model="receiptNumber" />
        </a-form-item>
      </div>
      <div class="col-md-6">
        <a-form-item :label="$t('comptabilite.description')">
          <a-input v-model="description" />
        </a-form-item>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <a-form-item :label="$t('recette.datePaiement')">
          <a-date-picker
            style="width: 100%"
            :value="paidAt ? moment(paidAt) : null"
            format="DD/MM/YYYY"
            @change="(v) => (paidAt = v ? v.toDate() : null)"
          />
        </a-form-item>
      </div>
    </div>

    <a-button v-if="!hideSubmit" type="primary" :loading="loading" @click="submit">
      {{ $t("action.enregistrer") }}
    </a-button>
  </div>
</template>

<script>
import moment from "moment";
import {
  findCashCaisseInLookups,
  findBankCaisseInLookups,
  availableDecaissementForMethod,
  cashLineAmountExceedsAvailability,
  bankLineAmountExceedsAvailability,
  lineAmountExceedsPaymentSource,
} from "@/utilities/depense/caisseDecaissementAvailability";
import PayChequeSection from "./paymentDetails/PayChequeSection.vue";
import "./paymentCaisseInsufficient.css";
import PayTraiteSection from "./paymentDetails/PayTraiteSection.vue";
import PayVirementSection from "./paymentDetails/PayVirementSection.vue";

export default {
  name: "ImmediatePaymentForm",
  components: {
    PayChequeSection,
    PayTraiteSection,
    PayVirementSection,
  },
  props: {
    depense: { type: Object, default: null },
    paymentSources: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    totalAmount: { type: Number, default: null },
    hideSubmit: { type: Boolean, default: false },
  },
  data() {
    return {
      paymentLines: [],
      receiptNumber: "",
      description: "",
      paidAt: new Date(),
    };
  },
  computed: {
    /** A user can own several caisses, all usable whatever the active school year. */
    myCaisses() {
      if (Array.isArray(this.paymentSources.myCaisses)) {
        return this.paymentSources.myCaisses;
      }
      return this.paymentSources.myCaisse ? [this.paymentSources.myCaisse] : [];
    },
    myCaisseId() {
      return this.myCaisses.length ? this.myCaisses[0]._id : undefined;
    },
    caisseBanksList() {
      return this.paymentSources.caisseBanks || [];
    },
    depenseCashCaisses() {
      return this.paymentSources.depenseCashCaisses || [];
    },
    depenseOtherCashOptions() {
      const mine = new Set(this.myCaisses.map((c) => String(c._id)));
      if (!mine.size) return this.depenseCashCaisses;
      return this.depenseCashCaisses.filter((c) => !mine.has(String(c._id)));
    },
    splitsSum() {
      return this.paymentLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    },
    splitsSumOk() {
      const t = this.resolvedTotal();
      if (t <= 0) return false;
      return Math.abs(this.splitsSum - t) < 0.01;
    },
  },
  watch: {
    totalAmount: {
      handler(v) {
        if (this.paymentLines.length === 1 && this.paymentLines[0]) {
          const line = { ...this.paymentLines[0], amount: v };
          this.$set(this.paymentLines, 0, line);
        }
      },
    },
    myCaisseId: {
      immediate: true,
      handler(newId) {
        if (!newId || !this.paymentLines.length) return;
        this.paymentLines = this.paymentLines.map((line) => {
          if (line.method !== "cash") return line;
          // When sources load after mount, ensure "my caisse" lines are effectively selected.
          if (line.fundDestination === "myCaisse" || !line.caisseId) {
            return {
              ...line,
              fundDestination: "myCaisse",
              caisseId: newId,
            };
          }
          return line;
        });
      },
    },
  },
  mounted() {
    this.reset();
  },
  methods: {
    moment,
    cashAvailabilityForCaisse(caisse, method) {
      const v = availableDecaissementForMethod(caisse, method);
      return v == null ? "—" : v.toFixed(3);
    },
    cashAvailabilityFormatted(line) {
      const c = findCashCaisseInLookups(this.paymentSources, line.caisseId);
      const v = availableDecaissementForMethod(c, line.method);
      return v == null ? "—" : v.toFixed(3);
    },
    lineCashAmountExceedsCaisse(line) {
      return cashLineAmountExceedsAvailability(this.paymentSources, line);
    },
    bankAvailabilityForCaisse(bank, method) {
      const v = availableDecaissementForMethod(bank, method);
      return v == null ? "—" : v.toFixed(3);
    },
    bankAvailabilityFormatted(line) {
      const b = findBankCaisseInLookups(this.paymentSources, line.caisseBankId);
      const v = availableDecaissementForMethod(b, line.method);
      return v == null ? "—" : v.toFixed(3);
    },
    lineBankAmountExceedsCaisse(line) {
      return bankLineAmountExceedsAvailability(this.paymentSources, line);
    },
    lineAmountExceedsAvailable(line) {
      return lineAmountExceedsPaymentSource(this.paymentSources, line);
    },
    resolvedTotal() {
      if (this.totalAmount != null && this.totalAmount > 0) {
        return Number(this.totalAmount);
      }
      return (
        this.depense?.amount || this.depense?.amountTTC || this.depense?.money || 0
      );
    },
    emptyChequeRow() {
      return { num: null, dateEchance: null, bank: undefined, titulaire: null, montant: 0 };
    },
    emptyTraiteRow() {
      return { num: null, dateEchance: null, bank: undefined, montant: 0 };
    },
    emptyVirementRow() {
      return { num: null, titulaire: null, montant: 0 };
    },
    defaultCashFundFields() {
      if (this.myCaisseId) {
        return { fundDestination: "myCaisse", caisseId: this.myCaisseId };
      }
      const first = this.depenseOtherCashOptions[0];
      if (first) return { fundDestination: "otherCaisse", caisseId: first._id };
      return { fundDestination: "myCaisse", caisseId: undefined };
    },
    createPaymentLine() {
      const cash = this.defaultCashFundFields();
      return {
        uid: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        method: "cash",
        amount: null,
        fundDestination: cash.fundDestination,
        caisseId: cash.caisseId,
        caisseBankId: undefined,
        chequeRows: [this.emptyChequeRow()],
        traiteRows: [this.emptyTraiteRow()],
        virementRows: [this.emptyVirementRow()],
      };
    },
    onLineFundDestinationChange(idx) {
      const line = { ...this.paymentLines[idx] };
      if (line.fundDestination === "myCaisse") {
        line.caisseId = this.myCaisseId;
      } else {
        const first = this.depenseOtherCashOptions[0];
        line.caisseId = first ? first._id : undefined;
      }
      this.$set(this.paymentLines, idx, line);
    },
    onLineMethodChange(idx) {
      const line = { ...this.paymentLines[idx] };
      line.chequeRows = [this.emptyChequeRow()];
      line.traiteRows = [this.emptyTraiteRow()];
      line.virementRows = [this.emptyVirementRow()];
      if (line.method === "cash") {
        const cash = this.defaultCashFundFields();
        line.fundDestination = cash.fundDestination;
        line.caisseId = cash.caisseId;
        line.caisseBankId = undefined;
      } else {
        line.caisseId = undefined;
        line.fundDestination = undefined;
        if (!line.caisseBankId && this.caisseBanksList.length) {
          line.caisseBankId = this.caisseBanksList[0]._id;
        }
      }
      this.$set(this.paymentLines, idx, line);
    },
    updateLineDetailRow(lineIdx, arrKey, rowIdx, field, val) {
      const line = { ...this.paymentLines[lineIdx] };
      const arr = [...line[arrKey]];
      arr[rowIdx] = { ...arr[rowIdx], [field]: val };
      line[arrKey] = arr;
      this.$set(this.paymentLines, lineIdx, line);
    },
    addLineDetailRow(lineIdx, arrKey) {
      const templates = {
        chequeRows: this.emptyChequeRow(),
        traiteRows: this.emptyTraiteRow(),
        virementRows: this.emptyVirementRow(),
      };
      const line = { ...this.paymentLines[lineIdx] };
      line[arrKey] = [...line[arrKey], { ...templates[arrKey] }];
      this.$set(this.paymentLines, lineIdx, line);
    },
    removeLineDetailRow(lineIdx, arrKey, rowIdx) {
      const line = { ...this.paymentLines[lineIdx] };
      line[arrKey] = line[arrKey].filter((_, i) => i !== rowIdx);
      this.$set(this.paymentLines, lineIdx, line);
    },
    detailSumForLine(line) {
      if (line.method === "cheque") {
        return (line.chequeRows || []).reduce((s, r) => s + (Number(r.montant) || 0), 0);
      }
      if (line.method === "traite") {
        return (line.traiteRows || []).reduce((s, r) => s + (Number(r.montant) || 0), 0);
      }
      if (line.method === "virement") {
        return (line.virementRows || []).reduce((s, r) => s + (Number(r.montant) || 0), 0);
      }
      return 0;
    },
    formatMethodDetailsRow(r) {
      return {
        ...r,
        dateEchance:
          r.dateEchance && r.dateEchance.format
            ? r.dateEchance.format("YYYY-MM-DD")
            : r.dateEchance || null,
      };
    },
    buildLineMethodDetails(line) {
      if (line.method === "cheque") {
        return { cheques: line.chequeRows.map((r) => this.formatMethodDetailsRow(r)) };
      }
      if (line.method === "traite") {
        return { traites: line.traiteRows.map((r) => this.formatMethodDetailsRow(r)) };
      }
      if (line.method === "virement") {
        return { virements: line.virementRows.map((r) => ({ ...r })) };
      }
      return {};
    },
    addPaymentLine() {
      const line = this.createPaymentLine();
      line.amount = 0;
      line.method = "cash";
      const cash = this.defaultCashFundFields();
      line.fundDestination = cash.fundDestination;
      line.caisseId = cash.caisseId;
      this.paymentLines = [...this.paymentLines, line];
    },
    removePaymentLine(idx) {
      if (this.paymentLines.length <= 1) return;
      this.paymentLines = this.paymentLines.filter((_, i) => i !== idx);
    },
    submit() {
      const payload = this.buildPayload();
      if (!payload) return;
      this.$emit("submit", payload);
    },
    buildPayload() {
      const total = this.resolvedTotal();
      if (!total || total <= 0) {
        this.$message.error(this.$t("decaissement.paymentTotalInvalid"));
        return null;
      }
      if (!this.splitsSumOk) {
        this.$message.error(this.$t("saisiePaiement.splitsSumMismatch"));
        return null;
      }
      for (let i = 0; i < this.paymentLines.length; i += 1) {
        const line = this.paymentLines[i];
        const amt = Number(line.amount);
        if (!amt || amt <= 0) {
          this.$message.error(this.$t("saisiePaiement.lineAmountRequired"));
          return null;
        }
        if (line.method === "cash") {
          if (!line.caisseId) {
            this.$message.error(
              this.$t("decaissement.validation.paymentCaisseRequired", {
                n: i + 1,
              })
            );
            return null;
          }
        } else if (!line.caisseBankId) {
          this.$message.error(
            this.$t("decaissement.validation.paymentBankRequired", {
              n: i + 1,
            })
          );
          return null;
        }
        if (["cheque", "traite", "virement"].includes(line.method)) {
          const ds = this.detailSumForLine(line);
          if (Math.abs(ds - amt) > 0.01) {
            this.$message.error(this.$t("decaissement.methodDetailsSumError"));
            return null;
          }
        }
      }

      const splits = this.paymentLines.map((line) => {
        const o = {
          method: line.method,
          amount: Number(line.amount),
          caisseId: line.method === "cash" ? line.caisseId : undefined,
          caisseBankId: line.method !== "cash" ? line.caisseBankId : undefined,
        };
        if (["cheque", "traite", "virement"].includes(line.method)) {
          o.methodDetails = this.buildLineMethodDetails(line);
        } else {
          o.methodDetails = {};
        }
        return o;
      });

      return {
        paymentMode: "immediate",
        totalToPay: total,
        splits,
        receiptNumber: this.receiptNumber || "",
        description: this.description || "",
        paidAt: this.paidAt || new Date(),
      };
    },
    reset() {
      const line = this.createPaymentLine();
      line.amount = this.resolvedTotal();
      this.paymentLines = [line];
      this.receiptNumber = "";
      this.description = "";
      this.paidAt = new Date();
    },
  },
};
</script>

<style scoped>
.filter-label {
  display: block;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 4px;
}

/* Full-width fund row: align with guttered cols above; label stacked left like other fields */
.payment-fund-stack {
  text-align: start;
}
.payment-fund-label {
  text-align: start;
  width: 100%;
}
.payment-split-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fafafa;
}
.sum-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #ffccc7;
  background: #fff2f0;
}
.sum-bar.match {
  border-color: #b7eb8f;
  background: #f6ffed;
}
</style>
